/**
 * AssociationRequestsPage - Gestione richieste di associazione
 * Per admin associazione: approva/rifiuta richieste, aggiungi membri diretti
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  AlertTriangle,
  Info,
  UserCheck,
  UserX,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AssociationRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  company_id?: string;
  company?: {
    name: string;
    vat_number?: string;
    address?: string;
    city?: string;
  };
  association_id: string;
  association_name: string;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
  created_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  rejection_reason?: string;
}

const AssociationRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<AssociationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  
  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState<AssociationRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  
  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [processing, setProcessing] = useState(false);

  // Carica TUTTI i dati una volta sola
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      // Carica tutte le richieste senza filtro
      const res = await axios.get(`${API}/association-requests/all`);
      setRequests(res.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Filtra lato client - istantaneo!
  const filteredRequests = statusFilter === 'all' 
    ? requests 
    : requests.filter(r => r.status === statusFilter);
  
  // Conta per i badge
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  const handleApprove = async (requestId: string) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/association-requests/${requestId}/approve`);
      toast.success('Richiesta approvata! L\'utente è ora membro dell\'associazione.');
      setDetailDialogOpen(false);
      fetchRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/association-requests/${selectedRequest.id}/reject`, {
        reason: rejectReason || null
      });
      toast.success('Richiesta rifiutata.');
      setRejectDialogOpen(false);
      setDetailDialogOpen(false);
      setRejectReason('');
      fetchRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddMemberDirect = async () => {
    if (!newMemberEmail) {
      toast.error('Inserisci l\'email');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/association-members/add-direct`, {
        email: newMemberEmail,
        name: newMemberName || null
      });
      toast.success('Membro aggiunto con successo!');
      setAddMemberDialogOpen(false);
      setNewMemberEmail('');
      setNewMemberName('');
      fetchRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta');
    } finally {
      setProcessing(false);
    }
  };

  const openRequestDetail = (request: AssociationRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: it });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-amber-600 border-amber-300"><Clock className="h-3 w-3 mr-1" />In attesa</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approvato</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rifiutato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="association-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Richieste di Associazione</h1>
          <p className="text-muted-foreground mt-1">
            Gestisci le richieste di nuovi membri
          </p>
        </div>
        <Button onClick={() => setAddMemberDialogOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Aggiungi Membro
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={pendingCount > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Attesa</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">richieste da esaminare</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approvate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {requests.filter(r => r.status === 'approved').length}
            </div>
            <p className="text-xs text-muted-foreground">questo mese</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rifiutate</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {requests.filter(r => r.status === 'rejected').length}
            </div>
            <p className="text-xs text-muted-foreground">questo mese</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri come Tabs - stesso stile di MembersManagementPage */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
        <TabsList className="grid w-full sm:w-auto grid-cols-4">
          <TabsTrigger value="all" className="gap-2">
            Tutte
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            In attesa
            <Badge variant="secondary" className="ml-1">{pendingCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Approvate
            <Badge variant="secondary" className="ml-1">{approvedCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-2">
            <XCircle className="h-4 w-4" />
            Rifiutate
            <Badge variant="secondary" className="ml-1">{rejectedCount}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Info Box */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Come funziona?</p>
              <ul className="mt-1 text-blue-700 space-y-1">
                <li>• <strong>Richieste approvate</strong>: l'utente mantiene tutte le funzioni buyer + guadagna quelle dell'associazione</li>
                <li>• <strong>Aggiungi Membro</strong>: l'utente avrà SOLO le funzioni dell'associazione (menu limitato)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Richieste */}
      <Card>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mb-4" />
              <p className="font-medium">Nessuna richiesta trovata</p>
              <p className="text-sm">
                {statusFilter === 'pending' 
                  ? 'Non ci sono richieste in attesa di approvazione'
                  : 'Cambia i filtri per vedere altre richieste'
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Data Richiesta</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{request.user_name}</p>
                        <p className="text-sm text-muted-foreground">{request.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.company ? (
                        <div>
                          <p className="font-medium">{request.company.name}</p>
                          {request.company.city && (
                            <p className="text-sm text-muted-foreground">{request.company.city}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(request.created_at)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openRequestDetail(request)}
                        >
                          Dettagli
                        </Button>
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => handleApprove(request.id)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approva
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => {
                                setSelectedRequest(request);
                                setRejectDialogOpen(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Rifiuta
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Dettaglio Richiesta */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettaglio Richiesta</DialogTitle>
            <DialogDescription>
              Richiesta di {selectedRequest?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Utente */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Informazioni Utente
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Nome:</span>
                    <p className="font-medium">{selectedRequest.user_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedRequest.user_email}</p>
                  </div>
                </div>
              </div>
              
              {/* Azienda */}
              {selectedRequest.company && (
                <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Azienda
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium">{selectedRequest.company.name}</p>
                    {selectedRequest.company.vat_number && (
                      <p className="text-muted-foreground">P.IVA: {selectedRequest.company.vat_number}</p>
                    )}
                    {selectedRequest.company.address && (
                      <p className="text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {selectedRequest.company.address}, {selectedRequest.company.city}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Note */}
              {selectedRequest.notes && (
                <div>
                  <Label>Note del richiedente</Label>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg mt-1">{selectedRequest.notes}</p>
                </div>
              )}
              
              {/* Stato */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Stato:</span>
                {getStatusBadge(selectedRequest.status)}
              </div>
              
              {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Motivo rifiuto:</strong> {selectedRequest.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Chiudi
            </Button>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button 
                  variant="outline"
                  className="text-red-600"
                  onClick={() => setRejectDialogOpen(true)}
                >
                  Rifiuta
                </Button>
                <Button onClick={() => handleApprove(selectedRequest.id)} disabled={processing}>
                  {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Approva
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Rifiuto */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rifiuta Richiesta</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rifiutare la richiesta di {selectedRequest?.user_name}?
              L'utente rimarrà come Buyer Standard senza accesso alle funzionalità dell'associazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label htmlFor="reject_reason">Motivo del rifiuto (opzionale)</Label>
            <Textarea
              id="reject_reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Es: Documentazione incompleta..."
              rows={2}
              className="mt-2"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={processing}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Rifiuta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Aggiungi Membro Diretto */}
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Membro Direttamente</DialogTitle>
            <DialogDescription>
              L'utente avrà accesso SOLO alle funzionalità dell'associazione (menu limitato).
              Non avrà le funzioni complete del buyer standard.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Questo utente avrà un menu limitato. Se vuoi che abbia tutte le funzioni,
                  chiedigli di registrarsi autonomamente e richiedere l'associazione.
                </span>
              </p>
            </div>
            
            <div>
              <Label htmlFor="member_email">Email *</Label>
              <Input
                id="member_email"
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="email@esempio.com"
              />
            </div>
            
            <div>
              <Label htmlFor="member_name">Nome (opzionale)</Label>
              <Input
                id="member_name"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                placeholder="Mario Rossi"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddMemberDirect} disabled={processing || !newMemberEmail}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <UserPlus className="h-4 w-4 mr-2" />
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssociationRequestsPage;
